import React, { useState, useEffect } from 'react';
import { Star, MapPin, ExternalLink } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const GoogleReviews = ({ placeId }) => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [placeInfo, setPlaceInfo] = useState(null);

  useEffect(() => {
    const loadGoogleMapsScript = () => {
      return new Promise((resolve, reject) => {
        if (window.google && window.google.maps) {
          resolve();
          return;
        }

        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
        script.async = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Google Maps'));
        document.head.appendChild(script);
      });
    };

    const fetchReviews = async () => {
      try {
        await loadGoogleMapsScript();

        const map = new window.google.maps.Map(document.createElement('div'));
        const service = new window.google.maps.places.PlacesService(map);

        service.getDetails(
          {
            placeId: placeId,
            fields: ['name', 'rating', 'reviews', 'user_ratings_total', 'url']
          },
          (place, status) => {
            if (status === window.google.maps.places.PlacesServiceStatus.OK) {
              setPlaceInfo({
                name: place.name,
                rating: place.rating,
                totalRatings: place.user_ratings_total,
                googleUrl: place.url
              });
              setReviews(place.reviews || []);
            }
            setLoading(false);
          }
        );
      } catch (error) {
        console.error('Error loading reviews:', error);
        setLoading(false);
      }
    };

    if (placeId) {
      fetchReviews();
    }
  }, [placeId]);

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#52a675] mx-auto"></div>
        <p className="mt-4 text-gray-600">Chargement des avis Google...</p>
      </div>
    );
  }

  if (!reviews.length) {
    return (
      <div className="text-center py-8 text-gray-500">
        Aucun avis disponible pour le moment.
      </div>
    );
  }

  const renderStars = (rating) => {
    return (
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`w-5 h-5 ${
              star <= rating
                ? 'fill-yellow-400 text-yellow-400'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-8">
      {/* Overall Rating */}
      {placeInfo && (
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="flex">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star
                  key={star}
                  className={`w-8 h-8 ${
                    star <= Math.round(placeInfo.rating)
                      ? 'fill-yellow-400 text-yellow-400'
                      : 'text-gray-300'
                  }`}
                />
              ))}
            </div>
            <span className="text-3xl font-bold text-gray-900">
              {placeInfo.rating.toFixed(1)}/5
            </span>
          </div>
          <p className="text-lg text-gray-600 mb-4">
            Basé sur {placeInfo.totalRatings} avis Google
          </p>
          {placeInfo.googleUrl && (
            <a
              href={placeInfo.googleUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#52a675] hover:underline font-semibold"
            >
              <MapPin className="w-4 h-4" />
              Voir sur Google Maps
              <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>
      )}

      {/* Reviews Grid */}
      <div className="grid md:grid-cols-3 gap-6">
        {reviews.slice(0, 6).map((review, index) => (
          <Card key={index} className="hover:shadow-xl transition-all">
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                {review.profile_photo_url && (
                  <img
                    src={review.profile_photo_url}
                    alt={review.author_name}
                    className="w-12 h-12 rounded-full"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">
                    {review.author_name}
                  </p>
                  {renderStars(review.rating)}
                </div>
              </div>

              <p className="text-gray-700 mb-4 line-clamp-4">
                {review.text}
              </p>

              <div className="text-sm text-gray-500">
                {review.relative_time_description}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* View All Button */}
      {placeInfo?.googleUrl && (
        <div className="text-center mt-8">
          <a
            href={placeInfo.googleUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-[#52a675] text-white px-6 py-3 rounded-lg hover:bg-[#3d8a5c] transition-colors font-semibold"
          >
            Voir tous les avis sur Google
            <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      )}
    </div>
  );
};

export default GoogleReviews;
